import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { cancelHelpApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { 
  MessageCircle, 
  Send, 
  Mail, 
  Shield, 
  Zap, 
  ArrowRight,
  CheckCircle,
  XCircle,
  HelpCircle,
  Loader2,
  Search
} from 'lucide-react';

// Popular services for quick selection and SEO
const POPULAR_SERVICES = [
  { name: 'Netflix', slug: 'netflix' },
  { name: 'Spotify', slug: 'spotify' },
  { name: 'Amazon Prime', slug: 'amazon-prime' },
  { name: 'Disney+', slug: 'disney-plus' },
  { name: 'YouTube Premium', slug: 'youtube-premium' },
  { name: 'Apple Music', slug: 'apple-music' },
  { name: 'iCloud', slug: 'icloud' },
  { name: 'Adobe', slug: 'adobe' },
  { name: 'Gym Membership', slug: 'gym' },
  { name: 'HelloFresh', slug: 'hellofresh' },
  { name: 'Audible', slug: 'audible' },
  { name: 'Hulu', slug: 'hulu' },
];

const CancelHelp = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const chatEndRef = useRef(null);
  const { login } = useAuth();
  
  // Get service from URL for SEO (e.g., /cancel-help?service=netflix)
  const serviceFromUrl = searchParams.get('service');
  
  // State
  const [step, setStep] = useState('landing'); // landing, email, otp, chat, limit-reached
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [selectedService, setSelectedService] = useState(serviceFromUrl || '');
  const [customService, setCustomService] = useState('');
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [questionsUsed, setQuestionsUsed] = useState(0);
  const [questionsRemaining, setQuestionsRemaining] = useState(2);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false);

  // Auto-scroll to bottom of chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  // If service is in URL, pre-fill and go to email step
  useEffect(() => {
    if (serviceFromUrl) {
      const service = POPULAR_SERVICES.find(s => s.slug === serviceFromUrl);
      if (service) {
        setSelectedService(service.name);
      } else {
        setCustomService(serviceFromUrl.replace(/-/g, ' '));
      }
    }
  }, [serviceFromUrl]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsLoading(true);
    try {
      const response = await cancelHelpApi.sendOtp(email);
      toast.success('Verification code sent to your email');
      setStep('otp');
      
      // For development - show OTP if returned
      if (response.data.debug_otp) {
        toast.info(`Dev OTP: ${response.data.debug_otp}`, { duration: 10000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) return;
    
    setIsLoading(true);
    try {
      const response = await cancelHelpApi.verifyOtp(email, otp);
      setSessionId(response.data.session_id);
      setQuestionsUsed(response.data.questions_used);
      setQuestionsRemaining(response.data.questions_remaining);
      setStep('chat');
      
      // Send initial greeting with service context
      const serviceName = selectedService || customService;
      if (serviceName) {
        // Auto-send first question about the selected service
        setTimeout(() => {
          setMessage(`How do I cancel ${serviceName}?`);
        }, 500);
      }
      
      toast.success('Verified! Ask your cancellation questions below.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || isSending) return;
    
    const userMessage = message.trim();
    const serviceName = selectedService || customService;
    
    // Add user message to chat immediately
    setChatHistory(prev => [...prev, { role: 'user', content: userMessage }]);
    setMessage('');
    setIsSending(true);
    
    try {
      const response = await cancelHelpApi.chat(sessionId, userMessage, serviceName);
      
      if (response.data.limit_reached && !response.data.response) {
        // Limit reached before getting response
        setStep('limit-reached');
      } else {
        // Add AI response
        setChatHistory(prev => [...prev, { role: 'assistant', content: response.data.response }]);
        setQuestionsUsed(response.data.questions_used);
        setQuestionsRemaining(response.data.questions_remaining);
        
        if (response.data.limit_reached) {
          // Limit reached after this response
          setTimeout(() => setStep('limit-reached'), 2000);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
      // Remove the user message if failed
      setChatHistory(prev => prev.slice(0, -1));
      setMessage(userMessage);
    } finally {
      setIsSending(false);
    }
  };

  const handleServiceSelect = (service) => {
    setSelectedService(service.name);
    setCustomService('');
    setStep('email');
  };

  const handleCustomServiceSubmit = (e) => {
    e.preventDefault();
    if (customService.trim()) {
      setSelectedService('');
      setStep('email');
    }
  };

  // One-click signup for verified cancel help users
  const handleQuickSignup = async () => {
    if (!sessionId) {
      toast.error('Session expired. Please start over.');
      return;
    }
    
    setIsSigningUp(true);
    try {
      const response = await cancelHelpApi.quickSignup(sessionId);
      
      // Log the user in
      login(response.data.token, response.data.user);
      
      toast.success(response.data.message || 'Account created! Welcome to SubGuard.');
      
      // Redirect to onboarding or dashboard
      if (response.data.onboarding_completed) {
        navigate('/dashboard');
      } else {
        navigate('/onboarding');
      }
    } catch (error) {
      console.error('Quick signup error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create account. Please try again.');
    } finally {
      setIsSigningUp(false);
    }
  };

  // SEO meta tags based on service
  const getMetaTags = () => {
    const serviceName = selectedService || customService || serviceFromUrl;
    if (serviceName) {
      return {
        title: `How to Cancel ${serviceName} - Free Cancellation Help | SubGuard`,
        description: `Get free step-by-step instructions to cancel your ${serviceName} subscription. Our AI expert will guide you through the cancellation process in minutes.`,
        keywords: `cancel ${serviceName}, how to cancel ${serviceName}, ${serviceName} cancellation, unsubscribe ${serviceName}, stop ${serviceName} subscription`
      };
    }
    return {
      title: 'How to Cancel Any Subscription - Free Cancellation Help | SubGuard',
      description: 'Get free expert help cancelling any subscription. Our AI assistant provides step-by-step cancellation instructions for Netflix, Spotify, Amazon Prime, gym memberships, and more.',
      keywords: 'cancel subscription, how to cancel, unsubscribe, stop subscription, cancellation help, cancel Netflix, cancel Spotify, cancel gym membership'
    };
  };

  const meta = getMetaTags();

  return (
    <>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta name="keywords" content={meta.keywords} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <link rel="canonical" href={`https://subguardhq.com/cancel-help${serviceFromUrl ? `?service=${serviceFromUrl}` : ''}`} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Header */}
        <header className="border-b border-gray-100 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <button onClick={() => navigate('/')} className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_96339eda-de9f-4afd-b0cc-aa940ad2aaf2/artifacts/doc4booq_SubGuard_icon.png" 
                alt="SubGuard" 
                className="w-8 h-8"
              />
              <span className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>SubGuard</span>
            </button>
            <Button 
              onClick={() => navigate('/login')}
              variant="outline"
              className="rounded-full"
            >
              Sign In
            </Button>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-8 md:py-12">
          
          {/* Landing Step - Service Selection */}
          {step === 'landing' && (
            <div className="space-y-8">
              {/* Hero */}
              <div className="text-center space-y-4">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D1FAE5] text-[#064E3B] rounded-full text-sm font-medium">
                  <Zap className="w-4 h-4" />
                  Free • No signup required • 2 questions
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
                  How Do I Cancel <span className="text-[#064E3B]">This?</span>
                </h1>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Get instant, expert help cancelling any subscription. Our AI assistant will guide you through the process step-by-step.
                </p>
              </div>

              {/* Service Search */}
              <Card className="border-2 border-gray-100">
                <CardContent className="p-6">
                  <form onSubmit={handleCustomServiceSubmit} className="space-y-4">
                    <div className="relative">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="What subscription do you want to cancel?"
                        value={customService}
                        onChange={(e) => setCustomService(e.target.value)}
                        className="pl-12 h-14 text-lg rounded-xl border-gray-200"
                        data-testid="service-search-input"
                      />
                    </div>
                    {customService && (
                      <Button 
                        type="submit" 
                        className="w-full h-12 bg-[#064E3B] hover:bg-[#043927] rounded-xl"
                        data-testid="get-help-btn"
                      >
                        Get Help Cancelling {customService}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    )}
                  </form>
                </CardContent>
              </Card>

              {/* Popular Services Grid */}
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-700 text-center">Or choose a popular service:</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {POPULAR_SERVICES.map((service) => (
                    <button
                      key={service.slug}
                      onClick={() => handleServiceSelect(service)}
                      className="p-4 bg-white border border-gray-200 rounded-xl hover:border-[#064E3B] hover:bg-[#F0FDF4] transition-all text-left group"
                      data-testid={`service-${service.slug}`}
                    >
                      <span className="font-medium text-gray-800 group-hover:text-[#064E3B]">{service.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500 pt-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  No credit card required
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Instant expert answers
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Works for any subscription
                </div>
              </div>
            </div>
          )}

          {/* Email Step */}
          {step === 'email' && (
            <div className="max-w-md mx-auto space-y-6">
              <button 
                onClick={() => setStep('landing')}
                className="text-gray-500 hover:text-gray-700 text-sm flex items-center gap-1"
              >
                ← Back
              </button>
              
              <div className="text-center space-y-2">
                <div className="w-16 h-16 bg-[#D1FAE5] rounded-full flex items-center justify-center mx-auto">
                  <Mail className="w-8 h-8 text-[#064E3B]" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Quick Verification</h2>
                <p className="text-gray-600">
                  Enter your email to get free cancellation help for <strong>{selectedService || customService}</strong>
                </p>
              </div>

              <form onSubmit={handleSendOtp} className="space-y-4">
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 rounded-xl"
                  required
                  data-testid="email-input"
                />
                <Button 
                  type="submit" 
                  className="w-full h-12 bg-[#064E3B] hover:bg-[#043927] rounded-xl"
                  disabled={isLoading}
                  data-testid="send-otp-btn"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Verification Code'}
                </Button>
              </form>

              <p className="text-xs text-gray-400 text-center">
                We'll send a 6-digit code to verify your email. No spam, ever.
              </p>
            </div>
          )}

          {/* OTP Step */}
          {step === 'otp' && (
            <div className="max-w-md mx-auto space-y-6">
              <button 
                onClick={() => setStep('email')}
                className="text-gray-500 hover:text-gray-700 text-sm flex items-center gap-1"
              >
                ← Back
              </button>
              
              <div className="text-center space-y-2">
                <div className="w-16 h-16 bg-[#D1FAE5] rounded-full flex items-center justify-center mx-auto">
                  <Shield className="w-8 h-8 text-[#064E3B]" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Enter Code</h2>
                <p className="text-gray-600">
                  We sent a 6-digit code to <strong>{email}</strong>
                </p>
              </div>

              <form onSubmit={handleVerifyOtp} className="space-y-4">
                <Input
                  type="text"
                  placeholder="000000"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="h-14 text-2xl text-center tracking-[0.5em] rounded-xl font-mono"
                  maxLength={6}
                  required
                  data-testid="otp-input"
                />
                <Button 
                  type="submit" 
                  className="w-full h-12 bg-[#064E3B] hover:bg-[#043927] rounded-xl"
                  disabled={isLoading || otp.length !== 6}
                  data-testid="verify-otp-btn"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & Start'}
                </Button>
              </form>

              <button 
                onClick={handleSendOtp}
                className="text-sm text-[#064E3B] hover:underline w-full text-center"
                disabled={isLoading}
              >
                Didn't receive it? Send again
              </button>
            </div>
          )}

          {/* Chat Step */}
          {step === 'chat' && (
            <div className="space-y-4">
              {/* Question Counter */}
              <div className="flex items-center justify-between bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <MessageCircle className="w-5 h-5 text-[#064E3B]" />
                  <span className="text-gray-700">
                    Cancellation Help: <strong>{selectedService || customService || 'Any Service'}</strong>
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className={questionsRemaining === 1 ? 'text-amber-600' : 'text-gray-500'}>
                    {questionsRemaining} question{questionsRemaining !== 1 ? 's' : ''} remaining
                  </span>
                  <div className="flex gap-1">
                    {[...Array(2)].map((_, i) => (
                      <div 
                        key={i} 
                        className={`w-2 h-2 rounded-full ${i < questionsUsed ? 'bg-gray-300' : 'bg-[#064E3B]'}`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Chat Area */}
              <Card className="border-2 border-gray-100 min-h-[400px] max-h-[500px] flex flex-col">
                <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
                  {/* Welcome Message */}
                  {chatHistory.length === 0 && (
                    <div className="bg-[#F0FDF4] rounded-xl p-4 text-gray-700">
                      <p className="font-medium text-[#064E3B] mb-2">Hi! I'm your Cancellation Expert.</p>
                      <p>Ask me how to cancel any subscription and I'll give you step-by-step instructions. What would you like help cancelling?</p>
                    </div>
                  )}
                  
                  {/* Chat Messages */}
                  {chatHistory.map((msg, index) => (
                    <div 
                      key={index} 
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div 
                        className={`max-w-[80%] rounded-xl p-4 ${
                          msg.role === 'user' 
                            ? 'bg-[#064E3B] text-white' 
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    </div>
                  ))}
                  
                  {/* Loading indicator */}
                  {isSending && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 rounded-xl p-4">
                        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                      </div>
                    </div>
                  )}
                  
                  <div ref={chatEndRef} />
                </CardContent>

                {/* Chat Input */}
                <div className="border-t border-gray-100 p-4">
                  <form onSubmit={handleSendMessage} className="flex gap-2">
                    <Input
                      type="text"
                      placeholder="Ask your cancellation question..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="flex-1 h-12 rounded-xl"
                      disabled={isSending || questionsRemaining <= 0}
                      data-testid="chat-input"
                    />
                    <Button 
                      type="submit" 
                      className="h-12 px-6 bg-[#064E3B] hover:bg-[#043927] rounded-xl"
                      disabled={isSending || !message.trim() || questionsRemaining <= 0}
                      data-testid="send-message-btn"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </form>
                </div>
              </Card>
            </div>
          )}

          {/* Limit Reached Step */}
          {step === 'limit-reached' && (
            <div className="max-w-lg mx-auto text-center space-y-6 py-8">
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                <HelpCircle className="w-10 h-10 text-amber-600" />
              </div>
              
              <div className="space-y-2">
                <h2 className="text-2xl font-bold text-gray-900">You've Used Your 2 Free Questions</h2>
                <p className="text-gray-600">
                  Want unlimited cancellation help plus automatic subscription tracking?
                </p>
              </div>

              <Card className="border-2 border-[#064E3B] bg-[#F0FDF4]">
                <CardContent className="p-6 space-y-4">
                  <h3 className="font-bold text-lg text-[#064E3B]">Sign Up Free & Get:</h3>
                  <ul className="space-y-3 text-left">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#064E3B] mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Unlimited AI cancellation assistance</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#064E3B] mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Scan your email for hidden subscriptions</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#064E3B] mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Track renewal dates & get alerts</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#064E3B] mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">See how much you're spending on subscriptions</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <div className="space-y-3">
                {/* One-click signup - email already verified */}
                <div className="bg-gray-50 rounded-xl p-4 mb-2">
                  <p className="text-sm text-gray-600 mb-1">Your email is already verified:</p>
                  <p className="font-medium text-gray-900">{email}</p>
                </div>
                
                <Button 
                  onClick={handleQuickSignup}
                  className="w-full h-14 bg-[#064E3B] hover:bg-[#043927] rounded-xl text-lg font-semibold"
                  disabled={isSigningUp}
                  data-testid="quick-signup-btn"
                >
                  {isSigningUp ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Create Free Account Instantly
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
                
                <p className="text-xs text-gray-500">
                  One click — no password needed, no extra steps
                </p>
                
                <button 
                  onClick={() => navigate('/pricing')}
                  className="text-sm text-gray-500 hover:text-[#064E3B]"
                >
                  View pricing plans →
                </button>
              </div>
            </div>
          )}

        </main>

        {/* Footer */}
        <footer className="border-t border-gray-100 mt-12 py-8">
          <div className="max-w-4xl mx-auto px-4 text-center text-sm text-gray-500">
            <p>© 2025 SubGuard. Free cancellation help powered by AI.</p>
            <div className="flex justify-center gap-4 mt-2">
              <button onClick={() => navigate('/privacy')} className="hover:text-gray-700">Privacy</button>
              <button onClick={() => navigate('/terms')} className="hover:text-gray-700">Terms</button>
              <button onClick={() => navigate('/')} className="hover:text-gray-700">Home</button>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default CancelHelp;
